import { CdkStepper, CdkStepperModule } from '@angular/cdk/stepper';
import { CommonModule, NgTemplateOutlet, ViewportScroller } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  ElementRef,
  HostBinding,
  HostListener,
  inject,
  Input,
  ViewChild,
} from '@angular/core';

/** Custom CDK stepper component */
@Component({
  selector: 'csl-stepper',
  standalone: true,
  imports: [CommonModule, NgTemplateOutlet, CdkStepperModule],
  templateUrl: './csl-stepper.component.html',
  styleUrl: './csl-stepper.component.css',
  providers: [{ provide: CdkStepper, useExisting: CslStepperComponent }],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'block',
  },
})
export class CslStepperComponent extends CdkStepper {
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly scroller = inject(ViewportScroller);
  private readonly elementRef = inject(ElementRef);

  private readonly anchorId = this.elementRef.nativeElement.getAttribute('id') || `stepper-${Math.random()}`;

  @HostBinding('id')
  private get _id() {
    return this.anchorId;
  }

  @Input() containerClasses = '';
  @Input() stepScrollPosition: 'top' | 'stepper' | 'none' = 'stepper';
  @Input() ariaLabel = 'Progress';

  @ViewChild('stepHeading') stepHeading!: ElementRef<HTMLElement>;
  @ViewChild('stepContent') stepContent!: ElementRef<HTMLElement>;

  private static readonly FOCUSABLE_SELECTOR =
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  readonly previousStepLabel = computed(() => {
    const index = this.selectedIndex;
    if (index > 0) {
      return this.steps.toArray()[index - 1]?.label;
    }
    return null;
  });

  @HostListener('selectionChange')
  stepSelectionChange() {
    switch (this.stepScrollPosition) {
      case 'top': {
        this.scroller.scrollToPosition([0, 0], { behavior: 'smooth' });
        break;
      }
      case 'stepper': {
        this.scroller.scrollToAnchor(this.anchorId, { behavior: 'smooth' });
        break;
      }
    }
    this.focusStepHeading();
  }

  selectStepByIndex(index: number): void {
    this.selectedIndex = index;
  }

  completeCurrentStep(): void {
    if (this.selected) {
      this.selected.completed = true;
    } else {
      console.warn('No step selected');
    }
  }

  completeAndMoveNextStep(): void {
    if (this.selected) {
      this.selected.completed = true;
      this.next();
    } else {
      console.warn('No step selected');
    }
  }

  resetCurrentStep(): void {
    if (this.selected) {
      this.steps
        .filter((step, index) => index >= this.selectedIndex)
        .forEach((step) => {
          step.reset();
        });

      this.cdr.markForCheck();
    } else {
      console.warn('No step selected');
    }
  }

  private focusStepHeading(): void {
    setTimeout(() => {
      const container = this.stepContent?.nativeElement;
      // Fall back to the container itself (not the disconnected sr-only span) so focus - and
      // subsequent Tab order - stays anchored inside the step even while its content is still loading.
      const target = this.getStepFocusTarget(container) ?? container ?? this.stepHeading?.nativeElement;
      if (!target) {
        return;
      }
      if (!target.hasAttribute('tabindex')) {
        target.setAttribute('tabindex', '-1');
      }
      target.focus({ preventScroll: true });
    }, 0);
  }

  /** Prefer the step's own heading so screen readers announce the new step context, falling back to its first focusable control. */
  private getStepFocusTarget(container: HTMLElement | undefined): HTMLElement | null {
    if (!container) {
      return null;
    }
    return (
      container.querySelector<HTMLElement>('h1, h2, h3, h4, h5, h6') ??
      container.querySelector<HTMLElement>(CslStepperComponent.FOCUSABLE_SELECTOR)
    );
  }
}
